/**
 * This file defines the custom navigation bar component for the React application.
 * It uses React-Bootstrap components and includes custom styles from Navbar.css.
 *
 * Author: Meng Li
 * Created on: 2024-09-30
 * Professor: Mejdi Eraslan
 * Client: Melisa Eraslan
 */

import React, { useEffect, useState } from 'react';
import { Navbar, Nav, NavDropdown } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { CiMenuFries } from "react-icons/ci";

const CustomNavbar = () => {
    // Get the current location and navigate to different pages
    const location = useLocation();
    // Use navigate to redirect to different pages
    const navigate = useNavigate();
    // State to control NavDropdown visibility on hover
    const [showDropdown, setShowDropdown] = useState(false);
    // State to control sidebar visibility
    const [showSidebar, setShowSidebar] = useState(false);
    // State to show toggle icon for smaller screens
    const [showToggleIcon, setShowToggleIcon] = useState(window.innerWidth < 1100);

    // Handle logo click to navigate to the Welcome page
    const handleLogoClick = () => {
        navigate('/welcome');
    };

    // Handle mouse enter and leave for NavDropdown
    const handleMouseEnter = () => setShowDropdown(true);
    const handleMouseLeave = () => setShowDropdown(false);
    const toggleSidebar = () => setShowSidebar(!showSidebar);
    const handleCloseSidebar = () => setShowSidebar(false);

    // Update showToggleIcon based on window width
    useEffect(() => {
        const handleResize = () => {
            setShowToggleIcon(window.innerWidth < 1100);
        };

        window.addEventListener('resize', handleResize);

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return (
        <>
            {/* Main navigation bar with React-Bootstrap components */}
            <Navbar className="custom-navbar">
                <Navbar.Brand onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
                    <img
                        src={require('../images/logo.png')}
                        className="custom-logo"
                        alt="Logo"
                    />
                </Navbar.Brand>
                {showToggleIcon ? (
                    <CiMenuFries className="custom-toggler-icon" onClick={toggleSidebar} />
                ) : (
                    <div className="nav-links">
                        <Nav className="ml-auto custom-nav-links">
                            <Nav.Link href="/welcome" className={location.pathname === '/welcome' ? 'active' : ''}>Welcome</Nav.Link>
                            <Nav.Link href="/map" className={location.pathname === '/map' ? 'active' : ''}>Map</Nav.Link>
                            <Nav.Link href="/directory" className={location.pathname === '/directory' ? 'active' : ''}>Directory</Nav.Link>
                            <Nav.Link href="/about" className={location.pathname === '/about' ? 'active' : ''}>About</Nav.Link>

                            <NavDropdown
                                title="Get Involved"
                                id="basic-nav-dropdown"
                                show={showDropdown}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                            >
                                <NavDropdown.Item href="#">Donate</NavDropdown.Item>
                                <NavDropdown.Item href="/contact">Contact</NavDropdown.Item>
                            </NavDropdown>

                            <div className="custom-search-bar">
                                <input
                                    type="text"
                                    placeholder="Search"
                                    className="search-input"
                                />
                                <IoSearchOutline className="search-icon" />
                            </div>
                            <Nav.Link href="/login" className={`login-link ${location.pathname === '/login' ? 'active' : ''}`}>Login</Nav.Link>
                        </Nav>
                    </div>
                )}
            </Navbar>

            {showSidebar && (
                <>
                    <div className="overlay" onClick={handleCloseSidebar}></div>

                    <div className="sidebar">
                        <div className="sidebar-header">
                            <img
                                src={require('../images/logo.png')}
                                className="sidebar-logo"
                                alt="Sidebar Logo"
                                onClick={() => {
                                    handleLogoClick();
                                    handleCloseSidebar();
                                }}
                            />
                            <IoClose className="close-btn" onClick={handleCloseSidebar} />
                        </div>
                        <Nav className="flex-column sidebar-links">
                            <Nav.Link
                                href="/welcome"
                                className={location.pathname === '/welcome' ? 'active' : ''}
                                onClick={handleCloseSidebar}
                            >
                                Welcome
                            </Nav.Link>
                            <Nav.Link
                                href="/map"
                                className={location.pathname === '/map' ? 'active' : ''}
                                onClick={handleCloseSidebar}
                            >
                                Map
                            </Nav.Link>
                            <Nav.Link
                                href="/directory"
                                className={location.pathname === '/directory' ? 'active' : ''}
                                onClick={handleCloseSidebar}
                            >
                                Directory
                            </Nav.Link>
                            <Nav.Link
                                href="/about"
                                className={location.pathname === '/about' ? 'active' : ''}
                                onClick={handleCloseSidebar}
                            >
                                About
                            </Nav.Link>
                            <NavDropdown title="Get Involved">
                                <NavDropdown.Item href="#">Donate</NavDropdown.Item>
                                <NavDropdown.Item href="/contact">Contact</NavDropdown.Item>
                            </NavDropdown>
                            <div className="custom-search-bar">
                                <input type="text" placeholder="Search" className="search-input" />
                                <IoSearchOutline className="search-icon" />
                            </div>
                            <Nav.Link
                                href="/login"
                                className={location.pathname === '/login' ? 'active' : ''}
                                onClick={handleCloseSidebar}
                            >
                                Login
                            </Nav.Link>
                        </Nav>
                    </div>
                </>
            )}
        </>
    );
};

export default CustomNavbar;
